/**
 * External Handling — shared types (VALE-841).
 *
 * "Handling" records that someone outside the firm is doing this piece of work —
 * an executor closing an account, a family member selling a car. It is deliberately
 * NOT the same fact as `notifiedExternally`, which means *the firm did this step
 * outside Vale*. Both can be true on one row and they mean opposite things about
 * whose work it is (EH-DI-004).
 */

import type {
  ConfidenceLevel,
  QuoteSource
} from '@/lib/services/ai-analysis/types';

/**
 * The per-firm flag every External Handling surface hides behind (D6).
 *
 * Declared once because several surfaces read it (Discovery, Inventory and the
 * institution detail panel), and a per-surface string literal is exactly the
 * second definition that drifts when the flag is renamed. Must match
 * `EXTERNAL_HANDLING_FLAG` in
 * `backend/src/matters/handling/external-handling.constants.ts`, which is what
 * actually gates the write endpoints; a mismatch shows the controls to a firm
 * whose every write then 403s.
 */
export const EXTERNAL_HANDLING_FEATURE_FLAG = 'external-handling';

/**
 * The person handling the work. Always a contact **on this matter** — a
 * `contact_matters` row, never a bare contact and never free text (EH-DR-001).
 */
export interface HandlingHandler {
  /** The `contact_matters` row id — the handler reference itself. */
  contactMatterId: number;
  /** The underlying contact id, for linking through to the contact record. */
  contactId: number;
  /** Organisation name, or the person's full name. */
  name: string;
}

export interface Handling {
  /** `null` means the firm is handling this through Vale — the default. */
  handledBy: HandlingHandler | null;
  handlingSetAt: string | null;
  /** The firm's file note on *what* is being done. Never who. */
  handlingNote: string | null;
  /** ISO date (yyyy-mm-dd) the firm expects the handover to be finished by. */
  handlingExpectedBy: string | null;
  /**
   * Whether `handlingExpectedBy` has passed (EH-010). **Server-computed, never
   * derived here from the date beside it.** "Today" means the firm's Australian
   * business day, and a browser in another timezone would flip a row overdue up to
   * a day early.
   */
  handlingOverdue: boolean;
}

/**
 * What a set/revert writes. Passing `handledByContactMatterId: null` takes the work
 * back into Vale and clears the note and expected-by date with it (EH-005).
 */
export interface SetHandlingRequest {
  handledByContactMatterId: number | null;
  handlingNote?: string | null;
  handlingExpectedBy?: string | null;
}

/**
 * The handling value of a row the firm is handling itself — the shape the backend
 * projects for every unhandled row, since `toHandlingResponseDto` returns an object
 * with a null `handledBy` rather than null itself.
 *
 * Exported because the projection is now REQUIRED on several row types
 * (`TodayItemRow`, `InstitutionRedemptionItem`), so every fixture needs the empty
 * value and each one spelling out four nulls is four chances to write a phantom
 * handler (VALE-1760). Frozen so a fixture cannot mutate the shared default.
 */
export const NOT_EXTERNALLY_HANDLED: Handling = Object.freeze({
  handledBy: null,
  handlingSetAt: null,
  handlingNote: null,
  handlingExpectedBy: null,
  // An unhandled row cannot be overdue: `handlingOverdue` (VALE-842) is derived
  // from an expected-by date, which only exists alongside a handler.
  handlingOverdue: false
});

/** True when this row is being handled by someone outside the firm. */
export function isExternallyHandled(
  handling: Handling | null | undefined
): boolean {
  return !!handling?.handledBy;
}

/**
 * True when the handover has passed its expected-by date (EH-010, VALE-842).
 *
 * Reads the server's flag rather than comparing the date here, for the reason on
 * `handlingOverdue` above. Exists as a named predicate anyway so call sites say
 * what they mean and a later shape change is one edit.
 */
export function isHandoverOverdue(
  handling: Handling | null | undefined
): boolean {
  return !!handling?.handlingOverdue;
}

/**
 * Whether the "N handled by others" affordance folds this row away (EH-007).
 *
 * **The one definition of what folding hides, and the reason it is here rather
 * than inline at each site.** Discovery folds in three places (institution rows,
 * institution-less asset rows, and the affordance's own count), and the count has
 * to match exactly what the two list filters remove or the header disagrees with
 * the page. Three copies of `!handling?.handledBy` is how that invariant broke the
 * first time it changed.
 *
 * An overdue handover is deliberately **not** folded: resurfacing it is the whole
 * of EH-010. It resurfaces on the surface it already sits on, still carrying its
 * handler badge, so it reads as "handed over, and late" rather than as ordinary
 * outstanding firm work. It does **not** re-enter the actionable set, which is a
 * separate decision owned server-side by `isActionable`: a resurfaced row still
 * generates no tasks, no auto-drafts and no sends, and the send guard still
 * refuses it.
 */
export function isFoldedByHandling(
  handling: Handling | null | undefined
): boolean {
  return isExternallyHandled(handling) && !isHandoverOverdue(handling);
}

// ─── AI-proposed handovers (VALE-1866) ────────────────────────────────

/**
 * Where the preview found a name a proposed handover uses. `in-review` means a
 * row in this same review creates it, which is the common case on a first
 * meeting or an intake form. Mirrors `ProposedHandoverResolution` in
 * `backend/src/matters/handling/proposals/dto/proposed-handover.dto.ts`.
 */
export type ProposedHandoverResolution =
  | 'existing'
  | 'in-review'
  | 'unresolved';

/**
 * A handover an AI extraction proposed: the artefact said someone outside the
 * firm is dealing with an institution or an asset. Nothing is written unless the
 * reviewer accepts it, and accepting goes through the same backend seam as the
 * manual handover.
 */
export interface PreviewProposedHandover {
  subjectKind: 'institution' | 'asset';
  subjectLabel: string;
  institutionName?: string;
  assetDescription?: string;
  subjectResolution: ProposedHandoverResolution;
  institutionId?: number;
  inventoryItemId?: number;
  /** Who the subject is already recorded as handled by, when it is. */
  currentHandlerName?: string;
  /** Positions in the preview's `inventoryItems[]` this handover renders on. */
  inventoryItemIndices: number[];
  /** The handler as the artefact named them. */
  handlerName: string;
  handlerResolution: ProposedHandoverResolution;
  /** Set when the handler is already a contact on the matter. */
  handledByContactMatterId?: number;
  /** Set when a contact row in this review creates the handler. */
  handlerContactIndex?: number;
  notes?: string;
  expectedBy?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/**
 * A handover the matter ALREADY records, on the review rows it covers
 * (VALE-1866). Read-only context the review draws as the row's plain badge.
 */
export interface CurrentHandover {
  /** Positions in the preview's `inventoryItems[]`. */
  inventoryItemIndices: number[];
  handling: Handling;
}

/** A contact already on the matter a reviewer may pick as the handler. */
export interface HandoverHandlerOption {
  contactMatterId: number;
  name: string;
}

/** An accepted handover, as the apply endpoints take it. */
export interface ApplyProposedHandover {
  institutionName?: string;
  assetDescription?: string;
  institutionId?: number;
  inventoryItemId?: number;
  /** Resolved by name on the backend, after the review's own contacts are written. */
  handlerName: string;
  /** Takes precedence over `handlerName` when set. */
  handledByContactMatterId?: number;
  notes?: string;
  expectedBy?: string;
}

/** What the apply did with the accepted handovers. */
export interface HandoversApplyOutcome {
  applied: number;
  skipped: Array<{ subject: string; reason: string }>;
}
